import { MEGA_BASE_2 } from '@/consts'
import formatBytes from '@/helpers/usage-helpers/format-data'

// Format a cents amount as a dollar string (e.g. 2600 -> "$26.00"). Returns ''
// for non-numeric input so callers can safely pass an absent price. A free plan
// (0 cents) intentionally renders "$0.00".
const formatPrice = (cents) => (typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '')

// Parse a comma-separated country-code string into a trimmed, empties-removed
// array (e.g. "AU, NZ ," -> ["AU","NZ"]).
const parseCountryList = (str) => (str || '').split(',').map(c => c.trim()).filter(Boolean)

// Map a single plan from POST /catalog/v2/recommend (a ProfilePlan) into the
// same UI plan shape mapTravelPlan produces, so the cart/checkout logic (which
// reads plan.Availabilities[0].{UUID,ProductCatalogEntryUUID,RetailName,...})
// works identically for recommend-sourced and catalog-sourced plans.
//
// `profile_plan` = one entry of PerZone[].Plans[]; `region` = the location the
// recommendation was requested for. Returns null when no Entry (no plan for
// that tier/location).
export function mapRecommendedPlan(profile_plan, region) {
    const entry = profile_plan && profile_plan.Entry
    if (!entry) return null

    const cents = profile_plan.PriceCents ?? 0
    const original_cents = profile_plan.OriginalPriceCents ?? cents
    const has_discount = original_cents > cents
    const data_mb = entry.DataAllowanceMB || profile_plan.PlanDataMB || 0

    return {
        id: entry.CatalogUUID,
        availability_uuid: entry.AvailabilityUUID,
        name: entry.Name,
        description: entry.Description || '',
        data_mb,
        data: data_mb ? formatBytes(data_mb * MEGA_BASE_2, false, 2) : '',
        voice_text: 'Not included',
        price_cents: cents,
        price_display: formatPrice(cents),
        original_price_cents: has_discount ? original_cents : undefined,
        original_price_display: has_discount ? formatPrice(original_cents) : '',
        discount_cents: has_discount ? original_cents - cents : undefined,
        applied_offer_name: profile_plan.AppliedOfferName || '',
        currency: 'NZD',
        regions: entry.Regions || [region],
        country_list: parseCountryList(entry.CountryList),
        period: entry.ValidityPeriod || '',
        // Reconstruct the Availabilities[0] shape the cart-save logic consumes,
        // so recommend-sourced plans purchase identically to catalog ones.
        UUID: entry.CatalogUUID,
        ProductType: entry.ProductType,
        Availabilities: [{
            UUID: entry.AvailabilityUUID,
            ProductCatalogEntryUUID: entry.CatalogUUID,
            RetailName: entry.Name,
            RetailDescription: entry.Description || '',
            RetailCents: cents,
        }],
    }
}

// Map a raw /catalog/v2/entries "travelMobileAddon" entry into the UI plan
// shape the travel-esim pages render. Mirrors the addDetailsToAvailablePlan.js
// convention used by the mobile plan cards.
//
// Data allowance: `ocs.PurchaseDataAllowance` is provided in MB (base-2, same
// contract as the mobile plans' RecurringDataAllowance). We reuse the shared
// formatBytes helper (which expects BYTES) by converting MB -> bytes via
// MEGA_BASE_2, so there is a single conversion function across the app.
export default function mapTravelPlan(entry) {
    const availability = entry.Availabilities?.[0] || {}
    const ch = entry.Characteristics || {}
    const cents = availability.RetailCents ?? 0
    const data_mb = parseInt(ch['ocs.PurchaseDataAllowance'], 10) || 0

    // Discount fields set on the entry by applyPriceOverrides (present only when
    // a discount applies). Carried through so the UI can show the original price
    // and the cart product can reference the applied offer.
    const original_cents = entry.origianl_price
    const discount_cents = entry.discount

    return {
        id: entry.UUID,
        availability_uuid: availability.UUID,
        name: availability.RetailName || entry.Name,
        description: availability.RetailDescription || entry.Description || '',
        data_mb,
        data: data_mb ? formatBytes(data_mb * MEGA_BASE_2, false, 2) : '',
        voice_text: 'Not included',
        price_cents: cents,
        price_display: formatPrice(cents),
        // Discount context (undefined when no discount applies)
        original_price_cents: original_cents,
        original_price_display: formatPrice(original_cents),
        discount_cents,
        DiscountUUID: entry.DiscountUUID,
        currency: 'NZD',
        regions: entry.Regions || [],
        country_list: parseCountryList(ch['ocs.CountryList']),
        period: ch['ocs.TimePeriodInterval'] || '',
        // Keep raw server fields so the cart/checkout can consume them later
        UUID: entry.UUID,
        ProductType: entry.ProductType,
        Characteristics: entry.Characteristics,
        Availabilities: entry.Availabilities,
    }
}
